const ACCOUNT_TYPE_OPTIONS = ['demo', 'live', 'wallet'];

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
}

function normalizeServers(payload) {
  const candidates = [
    payload?.servers,
    payload?.items,
    payload?.data,
    payload,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
    if (candidate && typeof candidate === 'object') {
      const values = Object.values(candidate);
      if (values.length > 0 && values.some((item) => item && typeof item === 'object')) {
        return values;
      }
    }
  }

  return [];
}

function normalizePlatforms(payload) {
  const source = payload?.platforms ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(source) ? source : (source && typeof source === 'object' ? Object.values(source) : []);

  return list
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.value || '').trim();
      if (!value) {
        return null;
      }

      const label = String(item.label || value).trim() || value;
      return { value, label, capabilities: item.capabilities ?? {} };
    })
    .filter(Boolean);
}

export function mapTradingServersResponseToDto(payload) {
  return normalizeServers(payload).map((item, index) => {
    const tradingPlatform = String(item?.tradingPlatform || item?.platform || '').trim();
    const name = String(item?.name || '').trim();
    const description = String(item?.description || '').trim();
    const dsn = String(item?.dsn || '').trim();
    const dealerLogin = String(item?.dealerLogin || '').trim();
    const dealerPassword = String(item?.dealerPassword || '').trim();
    const dealerDsn = String(item?.dealerDsn || '').trim();
    const proxy = String(item?.proxy || '').trim();
    const accountTypes = normalizeList(item?.accountTypes).map((value) => String(value || '').trim()).filter(Boolean);

    return {
      id: String(item?.id || item?.uid || `trading-server-${index}`),
      tradingPlatform,
      name,
      description,
      dsn,
      dealerLogin,
      dealerPassword,
      dealerDsn,
      proxy,
      accountTypes,
      hasManagerToken: item?.hasManagerToken === true,
    };
  });
}

export function mapPlatformsDictionaryResponseToDto(payload) {
  return normalizePlatforms(payload);
}

export function buildTradingServerPayloadFromForm(form) {
  const capabilities = form?.capabilities ?? {};
  const managerCredential = capabilities.credentialType === 'manager_token';
  const managerToken = String(form?.managerToken || '').trim();
  return {
    ...(managerCredential && managerToken ? { managerToken } : {}),
    tradingPlatform: String(form?.tradingPlatform || '').trim(),
    name: String(form?.name || '').trim(),
    description: String(form?.description || '').trim(),
    dsn: String(form?.dsn || '').trim(),
    dealerLogin: managerCredential ? null : String(form?.dealerLogin || '').trim(),
    dealerPassword: managerCredential ? null : String(form?.dealerPassword || '').trim(),
    dealerDsn: managerCredential ? null : String(form?.dealerDsn || '').trim(),
    proxy: String(form?.proxy || '').trim() || null,
    accountTypes: normalizeList(form?.accountTypes).map((value) => String(value || '').trim()).filter(Boolean),
  };
}

export function validateTradingServerPayload(payload, capabilities = {}) {
  const nextErrors = {};
  if (!payload.tradingPlatform) {
    nextErrors.tradingPlatform = 'This field is required.';
  }
  if (!payload.name) {
    nextErrors.name = 'This field is required.';
  }
  if (!payload.description) {
    nextErrors.description = 'This field is required.';
  }
  if (!payload.dsn) {
    nextErrors.dsn = 'This field is required.';
  }
  if (!Array.isArray(payload.accountTypes) || payload.accountTypes.length === 0) {
    nextErrors.accountTypes = 'Please select at least one account type.';
  }

  if (capabilities.allowedAccountTypes?.length && payload.accountTypes?.some(type => !capabilities.allowedAccountTypes.includes(type))) {
    nextErrors.accountTypes = `Supported account types: ${capabilities.allowedAccountTypes.join(', ')}.`;
  }
  return nextErrors;
}

export function mapTradingServerFieldErrors(requestError) {
  const nextFieldErrors = {};
  const details = Array.isArray(requestError?.errors) ? requestError.errors : [];

  details.forEach((item) => {
    const parameter = String(item?.parameter || '').trim();
    if (['tradingPlatform', 'name', 'description', 'dsn', 'dealerLogin', 'dealerPassword', 'dealerDsn', 'proxy', 'accountTypes', 'managerToken'].includes(parameter)) {
      nextFieldErrors[parameter] = String(item?.message || 'Invalid value').trim() || 'Invalid value';
    }
  });

  return nextFieldErrors;
}

export { ACCOUNT_TYPE_OPTIONS };
